import { HttpErrorResponse } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { EMPTY, Observable, combineLatest, merge, of, throwError, timer } from 'rxjs';
import {
    catchError,
    concatMap,
    filter,
    map,
    mergeMap,
    pairwise,
    retry,
    switchMap,
    take,
    withLatestFrom,
} from 'rxjs/operators';
import { Store } from '@ngrx/store';
import { logout } from '@modeso/aumico-lib-users-fe';
import { AumicoAbcpPollIntervals, AumicoAbcpPollIntervalsToken } from '../abcp-data.config';
import { closingDocumentActions } from '../actions/closing-document.actions';
import { projectAccountsActions } from '../actions/project-accounts.actions';
import { IClassificationSummary, IPeriodBounds } from '../models/project-account.model';
import { ProjectAccountsState } from '../reducer/project-accounts.reducer';
import {
    selectClassificationSummary,
    selectProjectAccounts,
    selectProjectAccountsProjectId,
    selectProjectAccountsStatus,
    selectReconciliationOpen,
    selectReconciliationPolling,
    selectReconciliationWatch,
    selectSelectedComparisonPeriods,
    selectStepAccountsStatus,
    selectWithZeroAccounts,
} from '../selectors/project-accounts.selectors';
import { selectPendingUploadFilenames } from '../selectors/closing-document.selectors';
import { ProjectAccountsService } from '../services/project-accounts.service';
import { isScanPending, SCAN_RETRY_MAX, scanPendingRetryDelayMs } from './accounting-file.errors';

/**
 * Three failed ticks IN A ROW end the reconciliation chain (#162 round 4, SRR F1) — the route belongs
 * to #160 and answers 404/403/5xx on any stack it has not shipped to yet, and "keep asking" with only
 * the tick ceiling as a brake means 24 silent requests over ten minutes against a route with no rate
 * limiter of its own (#160 F8). One failure is still a blip (case 12 survives); three is a decision.
 */
const RECONCILIATION_FAILURE_STREAK_LIMIT = 3;

const NOTHING = 0;

/** Ten seconds while the model may be about to answer. */
const POLL_FAST_MS = 10_000;
/** Thirty afterwards — the measured run took minutes, so a tighter loop buys nothing. */
const POLL_SLOW_MS = 30_000;
/** How long the fast cadence lasts. */
const CLASSIFICATION_FAST_WINDOW_MS = 60_000;
/**
 * The polling window. Past it the chain stops AND the surface says "outstanding" — one threshold for
 * both, because they are the same statement.
 */
const CLASSIFICATION_GIVE_UP_MS = 10 * 60_000;

/**
 * How long the oldest outstanding request has been waiting, in ms.
 *
 * `0` when nothing is stamped: an absent timestamp beside a non-zero pending count is a backend that
 * has not answered yet, and treating that as "waited forever" would give up on the first tick.
 */
function ageOf(oldestRequestedAt: string | null): number {
    if (oldestRequestedAt === null) {
        return NOTHING;
    }

    const requested = Date.parse(oldestRequestedAt);

    return Number.isNaN(requested) ? NOTHING : Date.now() - requested;
}

/**
 * Open halves in a summary, or `-1` when there is no summary.
 *
 * NOT zero for `null`, deliberately: a closing switch clears the summary, and a "fell to zero"
 * transition must not be read out of a slice that simply forgot what it knew.
 */
function openHalvesOf(summary: IClassificationSummary | null): number {
    return summary === null ? -1 : summary.groupPending + summary.functionPending;
}

/**
 * Open GROUP halves only — what the account list's contents depend on.
 *
 * The list is grouped by position, so a settled group is what changes the table. A function is not
 * rendered on this surface at all, and waiting for it before refetching left the screen showing the
 * pre-classification grouping indefinitely: measured on 24.08.2026 with
 * `groupPending: 0, functionPending: 1`, where one account's function half never resolved and the
 * list was therefore never reloaded.
 */
function openGroupsOf(summary: IClassificationSummary | null): number {
    return summary === null ? -1 : summary.groupPending;
}

/**
 * What a failed call is allowed to say.
 *
 * The backend answers a stable key, never prose about its internals. Anything else becomes one
 * generic key, so a driver or storage message can never reach a screen.
 *
 * An Accounto refusal (REV-21119 E2) carries its key too — aumico-be answers 422 `{ errorCode, errorMessage,
 * messageKey }` (QRM A2) — so this generic lib keeps no copy of a vendor's error map.
 */
function messageKeyOf(cause: unknown): string {
    if (cause instanceof HttpErrorResponse) {
        const body = cause.error as { messageKey?: unknown } | null;

        return typeof body?.messageKey === 'string' ? body.messageKey : 'abcp_error_mandate_data_unavailable';
    }

    return 'abcp_error_mandate_data_unavailable';
}

@Injectable()
export class ProjectAccountsEffects {
    public constructor(
        private readonly actions$: Actions,
        private readonly service: ProjectAccountsService,
        private readonly store: Store<ProjectAccountsState>,
        @Inject(AumicoAbcpPollIntervalsToken) private readonly intervals: AumicoAbcpPollIntervals,
    ) {}

    /**
     * The cache guard: the page asks, this decides whether the backend has to be asked.
     *
     * Every entry to the balance-sheet or income-statement screen used to cost a full list read, and
     * the list decrypts a journal blob PER PERIOD to aggregate balances. Switching between the two
     * views of the same closing therefore paid for the same decryption twice, for identical data —
     * both screens read the same flat list and only their presenter differs.
     *
     * THERE IS NO CACHE KEY, and that is deliberate. The list depends on the query
     * (`comparisonPeriods` + `withZeroAccounts`), so the obvious design is a fingerprint of it — but
     * only two actions can change that query, `selectComparisonPeriods` and `setWithZeroAccounts`,
     * and BOTH already refetch through their own effects below. The state can therefore never hold
     * accounts that disagree with the query beside them, so `'loaded'` is sufficient on its own. A
     * hand-built fingerprint would be a second source for a fact the state already guarantees, and a
     * query field added later would silently not participate in it — serving stale rows with nothing
     * failing.
     *
     * THE PENDING CONDITION is the classification guard. An account is bound to a balance-sheet
     * position by a classifier that answers OUT OF BAND: the ERP sync requests classification and
     * does not wait, so rows can sit in `PENDING` and flip to `CLASSIFIED` with no client action in
     * between. Nothing polls for that today, which means re-entering the screen — and the full reload
     * it currently triggers — is the only way a user ever sees it finish. Caching that away would
     * freeze the screen with no explanation. So the cache is only used once the closing is SETTLED;
     * while anything is pending the behaviour is exactly what it is today, which makes this strictly
     * an improvement and never a regression. `null` counts as unsettled: a summary we have not read
     * yet says nothing.
     */
    public loadAccountsIfNeeded$ = createEffect(() =>
        this.actions$.pipe(
            ofType(projectAccountsActions.loadAccountsIfNeeded),
            withLatestFrom(
                this.store.select(selectProjectAccountsProjectId),
                this.store.select(selectProjectAccountsStatus),
                this.store.select(selectClassificationSummary),
                this.view$(),
            ),
            filter(([{ projectId }, loadedFor, status, summary]) => {
                // Both halves, and per half is the only correct reading: an account can be waiting
                // on its function while its group has been settled for minutes.
                //
                // ⚠️ This read `summary.pending` until 24.08.2026, a field the backend had already
                // renamed. `undefined === 0` is false, so the guard treated every closing as
                // unsettled and the cache never engaged — silently, with nothing failing. That is
                // exactly the drift a mirrored wire interface cannot catch.
                const settled = summary !== null && summary.groupPending + summary.functionPending === 0;

                // A request already in flight is left alone rather than restarted: `switchMap` below
                // would cancel and re-issue it for the same answer.
                return !(projectId === loadedFor && (status === 'loaded' || status === 'loading') && settled);
            }),
            // The CURRENT view travels with it. Without that the request would answer the backend's
            // default — prior year alone, zero accounts filtered — and re-entering the screen would
            // collapse the columns the user chose while the chips still showed them.
            map(([{ projectId }, , , , view]) => projectAccountsActions.loadAccounts({ projectId, ...view })),
        ),
    );

    /**
     * The list. `switchMap`, so opening a second closing cancels the first request rather than
     * racing it — the reducer drops a late answer anyway, and cancelling saves a journal decryption.
     */
    public loadAccounts$ = createEffect(() =>
        this.actions$.pipe(
            ofType(projectAccountsActions.loadAccounts),
            switchMap(({ projectId, comparisonPeriods, withZeroAccounts }) =>
                this.service.getAccounts(projectId, comparisonPeriods, withZeroAccounts).pipe(
                    map((response) => projectAccountsActions.loadAccountsSuccess({ projectId, response })),
                    catchError((cause: unknown) =>
                        of(projectAccountsActions.loadAccountsFailure({ projectId, error: messageKeyOf(cause) })),
                    ),
                ),
            ),
        ),
    );

    /**
     * The navigation's cache guard for the DEFAULT list (`stepAccounts`).
     *
     * Nothing is asked when the step list is there or on its way. When the shared list already IS the
     * default view (nothing switched on, no period chosen) it is taken over — the same request would
     * decrypt the same journal twice. Otherwise the default view is fetched on its own.
     *
     * No freshness check of its own: `refreshStepAccounts$` and the take-over in the reducer keep it as
     * fresh as the shared list, which every server-side change already reloads.
     */
    public loadStepAccountsIfNeeded$ = createEffect(() =>
        this.actions$.pipe(
            ofType(projectAccountsActions.loadStepAccountsIfNeeded),
            withLatestFrom(
                this.store.select(selectProjectAccountsProjectId),
                this.store.select(selectStepAccountsStatus),
                this.store.select(selectProjectAccountsStatus),
                this.store.select(selectProjectAccounts),
                this.view$(),
            ),
            mergeMap(([{ projectId }, loadedFor, stepStatus, accountsStatus, accounts, view]) => {
                const sameClosing = projectId === loadedFor;
                if (sameClosing && (stepStatus === 'loaded' || stepStatus === 'loading')) {
                    return EMPTY;
                }
                const isDefaultView = !view.withZeroAccounts && view.comparisonPeriods.length === NOTHING;
                if (sameClosing && isDefaultView && accountsStatus === 'loaded') {
                    return of(projectAccountsActions.loadStepAccountsSuccess({ projectId, accounts }));
                }

                return of(projectAccountsActions.loadStepAccounts({ projectId }));
            }),
        ),
    );

    /** The default list. `switchMap`, like the shared one: a second closing cancels the first request. */
    public loadStepAccounts$ = createEffect(() =>
        this.actions$.pipe(
            ofType(projectAccountsActions.loadStepAccounts),
            switchMap(({ projectId }) =>
                this.service.getAccounts(projectId).pipe(
                    map((response) =>
                        projectAccountsActions.loadStepAccountsSuccess({ projectId, accounts: response.accounts }),
                    ),
                    catchError((cause: unknown) =>
                        of(projectAccountsActions.loadStepAccountsFailure({ projectId, error: messageKeyOf(cause) })),
                    ),
                ),
            ),
        ),
    );

    /**
     * The shared list moved while it shows more than the default view (zero accounts on, periods
     * chosen): the reducer cannot take it over then, so the default list is fetched again — but only
     * when somebody has asked for it, an untouched `idle` list costs nothing.
     */
    public refreshStepAccounts$ = createEffect(() =>
        this.actions$.pipe(
            ofType(projectAccountsActions.loadAccountsSuccess),
            withLatestFrom(this.store.select(selectStepAccountsStatus), this.view$()),
            filter(
                ([, stepStatus, view]) =>
                    stepStatus !== 'idle' && (view.withZeroAccounts || view.comparisonPeriods.length > NOTHING),
            ),
            map(([{ projectId }]) => projectAccountsActions.loadStepAccounts({ projectId })),
        ),
    );

    /**
     * What the screen is currently showing, as the arguments a reload needs.
     *
     * One place, because every refetch has to carry it: a reload that drops it answers the backend's
     * default instead of what the user chose, and the columns change under them for a reason they
     * cannot see.
     */
    private view$(): Observable<{ comparisonPeriods: IPeriodBounds[]; withZeroAccounts: boolean }> {
        return combineLatest([
            this.store.select(selectSelectedComparisonPeriods),
            this.store.select(selectWithZeroAccounts),
        ]).pipe(
            map(([comparisonPeriods, withZeroAccounts]) => ({
                comparisonPeriods: [...comparisonPeriods],
                withZeroAccounts,
            })),
        );
    }

    /**
     * The periods we hold. `switchMap` for the same reason as the list, and it is cheap either way —
     * metadata only, no journal.
     */
    public loadJournalPeriods$ = createEffect(() =>
        this.actions$.pipe(
            ofType(projectAccountsActions.loadJournalPeriods),
            switchMap(({ projectId }) =>
                this.service.getJournalPeriods(projectId).pipe(
                    map((state) => projectAccountsActions.loadJournalPeriodsSuccess({ projectId, state })),
                    catchError((cause: unknown) =>
                        of(projectAccountsActions.loadJournalPeriodsFailure({ projectId, error: messageKeyOf(cause) })),
                    ),
                ),
            ),
        ),
    );

    /**
     * A chip was clicked, so the list is refetched for the new set.
     *
     * The reload is an EFFECT of the selection rather than something the page dispatches itself: the
     * page would otherwise have to know that changing a chip costs a request, and every caller would
     * have to remember it.
     */
    public reloadAfterSelection$ = createEffect(() =>
        this.actions$.pipe(
            ofType(projectAccountsActions.selectComparisonPeriods),
            withLatestFrom(this.store.select(selectWithZeroAccounts)),
            map(([{ projectId, comparisonPeriods }, withZeroAccounts]) =>
                projectAccountsActions.loadAccounts({ projectId, comparisonPeriods, withZeroAccounts }),
            ),
        ),
    );

    /**
     * The zero filter was switched. A refetch, not a local hide: the filter runs in the backend, over
     * figures this library only ever sees for the rows it was given.
     *
     * `withLatestFrom` keeps the chosen comparison periods, which would otherwise be dropped by the
     * reload and silently reset the columns to the backend's default prior year.
     */
    public reloadAfterZeroToggle$ = createEffect(() =>
        this.actions$.pipe(
            ofType(projectAccountsActions.setWithZeroAccounts),
            withLatestFrom(this.store.select(selectSelectedComparisonPeriods)),
            map(([{ projectId, withZeroAccounts }, comparisonPeriods]) =>
                // The toggle's own value, not the store's: the reducer has already applied it, but
                // reading it back through a second stream would depend on which fired first.
                projectAccountsActions.loadAccounts({
                    projectId,
                    comparisonPeriods: [...comparisonPeriods],
                    withZeroAccounts,
                }),
            ),
        ),
    );

    /**
     * The classification poll — and it SCHEDULES ITSELF rather than holding an interval.
     *
     * Each answer that still reports open halves asks for the next one. Nothing is subscribed for the
     * lifetime of the app, so there is no timer to remember to stop: the chain simply ends when the
     * counters reach zero, when the answer is overdue, or when the closing changes. That last one
     * matters here more than usual — this slice is a ROOT feature and outlives every page, so an
     * `interval()` without a stop signal would keep polling while the user is in another microservice.
     *
     * THE OVERDUE DECISION USES `oldestRequestedAt`, not how long this chain has been running. A user
     * who comes back after twenty minutes must see "outstanding", not start a fresh countdown on a
     * request that is long gone — and there is no scheduler on the backend that would notice. Same
     * threshold for the stop and for the message, so there is one number and not two.
     *
     * The cadence follows the measured run: the deterministic range table is already done inside the
     * sync, so what the poll waits for is a model call that took minutes. Ten seconds while it is
     * plausibly about to answer, thirty afterwards.
     */
    public pollClassification$ = createEffect(() =>
        this.actions$.pipe(
            ofType(projectAccountsActions.loadSummarySuccess),
            withLatestFrom(this.store.select(selectProjectAccountsProjectId)),
            // A late answer for a closing the user has left must not schedule anything. Without this
            // the next tick would dispatch `loadSummary` for the OLD closing, and that action applies
            // `forNewProject` — it would wipe the closing now on screen and relabel the slice.
            filter(([{ projectId }, current]) => projectId === current),
            switchMap(([{ projectId, summary }]) => {
                const open = summary.groupPending + summary.functionPending;
                if (open === NOTHING) {
                    return EMPTY;
                }

                const waitedMs = ageOf(summary.oldestRequestedAt);
                if (waitedMs >= CLASSIFICATION_GIVE_UP_MS) {
                    return of(projectAccountsActions.classificationOverdue({ projectId }));
                }

                const delay = waitedMs < CLASSIFICATION_FAST_WINDOW_MS ? POLL_FAST_MS : POLL_SLOW_MS;

                return timer(delay).pipe(map(() => projectAccountsActions.loadSummary({ projectId })));
            }),
        ),
    );

    /**
     * The one refetch of the expensive list, at the one moment it is safe.
     *
     * The list is NEVER replaced on a poll tick: it is grouped by position, so a newly classified
     * account physically moves to another group and the rows jump under whoever is reading them. But
     * when the counters reach zero the banner flips from "the AI is working" to a result, and a table
     * still showing the pre-classification grouping beside that banner is worse than a jump.
     *
     * Safe precisely there: the statement pages assign nothing, a Kontenplan cell the AI is still on
     * offers no picker, and the Kontenplan keeps choices still in flight on top of the list.
     *
     * Guarded on the PREVIOUS state having been unsettled, so a plain screen open — where the counters
     * are already zero — costs no second request.
     */
    public reloadAfterClassification$ = createEffect(() =>
        this.store.select(selectClassificationSummary).pipe(
            // `pairwise`, so the condition is the TRANSITION and not the state: the list is refetched
            // the moment the open count falls to zero, and a plain screen open — counters already zero
            // — costs no second request. An effect field remembering "we were polling" would be the
            // same thing with a place for it to go wrong.
            pairwise(),
            // The GROUP half decides, not both: the table is grouped by position, and a function that
            // is still open must not hold that refetch hostage. The Kontenplan (REV-22479) shows the
            // function too, so the moment the LAST half settles is a second reason to refetch — one
            // emission either way, since both can only become true on the same transition once.
            filter(
                ([before, after]) =>
                    (openGroupsOf(before) > NOTHING && openGroupsOf(after) === NOTHING) ||
                    (openHalvesOf(before) > NOTHING && openHalvesOf(after) === NOTHING),
            ),
            withLatestFrom(this.store.select(selectProjectAccountsProjectId), this.view$()),
            filter(([, projectId]) => projectId !== null),
            map(([, projectId, view]) =>
                projectAccountsActions.loadAccounts({ projectId: projectId as string, ...view }),
            ),
        ),
    );

    /**
     * The reconciliation poll's ENTRY POINT — #162 (AP8a).
     *
     * Rides `loadAccountsIfNeeded` rather than `loadAccountsSuccess`: that action fires on EVERY entry
     * to the accounts screen, cache hit or not (plan:GH-162 P2). The list's own cache guard may
     * legitimately serve a re-entry from the store with no request at all, and the reconciliation poll
     * must not depend on a fresh list request to notice the screen was re-entered.
     *
     * THE DOUBLE-START GUARD is `reconciliationPolling`, and only for the SAME closing (spec:GH-162 S1
     * — read case 8 and case 11 together): a chain that has ENDED (ceiling, or nothing left
     * outstanding) is not a chain, so re-entering with an account still outstanding starts a NEW one.
     * A different closing is never blocked by a stale flag: `projectId === current` is false for it, so
     * the guard does not apply, and `forNewProject` (reducer) clears the flag the moment the list itself
     * moves to the new closing.
     */
    public startReconciliationPoll$ = createEffect(() =>
        this.actions$.pipe(
            // The navigation asks for the default list (`loadStepAccountsIfNeeded`) and not the shared
            // one, and it is what used to keep this poll alive on every screen of a started closing.
            ofType(projectAccountsActions.loadAccountsIfNeeded, projectAccountsActions.loadStepAccountsIfNeeded),
            withLatestFrom(
                this.store.select(selectReconciliationPolling),
                this.store.select(selectProjectAccountsProjectId),
            ),
            filter(([{ projectId }, polling, current]) => !(polling && projectId === current)),
            map(([{ projectId }]) =>
                projectAccountsActions.loadReconciliationStatus({
                    projectId,
                    tick: 1,
                    elapsedMs: 0,
                    consecutiveFailures: 0,
                }),
            ),
        ),
    );

    /**
     * REV-22003 — the AI's DIRECT assignment of a document this session uploaded.
     *
     * The backend reconciles after every assignment path (`EVIDENCE_ASSIGNED`), but the FE only watched
     * the two a human makes: an auto-assigned account never entered `reconciliationWatch`, so its result
     * reached the screen only on a reload. The post-upload poll already re-reads the list until each new
     * file has left `open`; the row it then sees — `assigned` by the `agent`, one of THIS batch's
     * filenames — is the same signal a manual assign's success is.
     *
     * Correlated with the upload on purpose, not "any agent-assigned row": an old auto-assignment whose
     * account is still `NOT_ATTEMPTED` would otherwise keep a poll running to the ceiling on every
     * entry — exactly the case the watch exists to prevent (case 22).
     */
    public watchAgentAssignments$ = createEffect(() =>
        this.actions$.pipe(
            ofType(closingDocumentActions.loadDocumentsSuccess),
            withLatestFrom(
                this.store.select(selectPendingUploadFilenames),
                this.store.select(selectProjectAccountsProjectId),
            ),
            filter(([, pending, projectId]) => pending.length > NOTHING && projectId !== null),
            map(([{ response }, pending, projectId]) => ({
                projectId: projectId as string,
                accountIds: response.documents.flatMap((document) =>
                    pending.includes(document.filename) &&
                    document.assignment === 'assigned' &&
                    document.assignedBy === 'agent' &&
                    document.account !== null
                        ? [document.account.id]
                        : [],
                ),
            })),
            filter(({ accountIds }) => accountIds.length > NOTHING),
            map(({ projectId, accountIds }) => projectAccountsActions.watchReconciliation({ projectId, accountIds })),
        ),
    );

    /**
     * A new window needs a running chain. Same double-start guard as {@link startReconciliationPoll$}:
     * a chain already ticking for this closing picks the account up on its next answer.
     */
    public startPollOnWatch$ = createEffect(() =>
        this.actions$.pipe(
            ofType(projectAccountsActions.watchReconciliation),
            withLatestFrom(
                this.store.select(selectReconciliationPolling),
                this.store.select(selectProjectAccountsProjectId),
            ),
            filter(([{ projectId }, polling, current]) => projectId === current && !polling),
            map(([{ projectId }]) =>
                projectAccountsActions.loadReconciliationStatus({
                    projectId,
                    tick: 1,
                    elapsedMs: 0,
                    consecutiveFailures: 0,
                }),
            ),
        ),
    );

    /**
     * The reconciliation counter route itself. Failures are turned into their own action rather than
     * silently swallowed — `pollReconciliation$` below decides what a failure means for the chain.
     */
    public loadReconciliationStatus$ = createEffect(() =>
        this.actions$.pipe(
            ofType(projectAccountsActions.loadReconciliationStatus),
            switchMap(({ projectId, tick, elapsedMs, consecutiveFailures }) =>
                this.service.getReconciliationStatus(projectId).pipe(
                    map((status) =>
                        projectAccountsActions.loadReconciliationStatusSuccess({
                            projectId,
                            tick,
                            elapsedMs,
                            consecutiveFailures,
                            status,
                        }),
                    ),
                    catchError(() =>
                        of(
                            projectAccountsActions.loadReconciliationStatusFailure({
                                projectId,
                                tick,
                                elapsedMs,
                                consecutiveFailures,
                            }),
                        ),
                    ),
                ),
            ),
        ),
    );

    /**
     * The reconciliation poll's CADENCE — a self-scheduling `timer` chain, exactly like
     * `pollClassification$` (spec:GH-162 S7): every tick is planned AFTER the previous one's answer, so
     * the chain can never outrun a slow backend and the tests' upper bounds hold.
     *
     * Listens to success, failure AND `logout.success` (spec:GH-162 S5, round 4 SRR F2) in one merged
     * source: all three are things that can end or re-plan the chain, and folding `logout.success` into
     * the SAME `switchMap` this way is what lets it CANCEL a pending `timer()` outright rather than
     * merely being raced against it — the same cancellation a fresh entry already gets for free from
     * `switchMap` when another tick's answer supersedes it.
     *
     * A failed tick here does NOT end the chain on its own. The counter route belongs to #160 and does
     * not exist on `development` yet, so every FE that ships before it answers 404 on every single tick;
     * the chain has to survive a BLIP, stay quiet (no thrown error reaches the screen — `catchError`
     * above already saw to that) — but round 4 (SRR F1) draws the line at THREE consecutive failures:
     * past that the route is answering "no", not "not yet", and hammering it for the remaining ceiling
     * is exactly the invisible load F1 found. The end is logged once, with no closing/account id and no
     * token — the line sits where Sentry's `ErrorHandler` can see it.
     *
     * THE STOP VALUE (spec:GH-162 S2, `POLL_STOPS_WHEN` in the e2e fixture) is FE-only and read in the
     * POSITIVE direction ONLY: "open" is `reconciliation === 'NOT_ATTEMPTED'` on at least one account
     * THIS SESSION IS WATCHING (`selectReconciliationWatch`) that THIS CLOSING'S OWN LIST ALREADY KNOWS
     * ABOUT. That last clause is round 4 (SRR F2, case 15): the counter route is keyed by `projectId`
     * in its URL like any other, but nothing stops an answer from naming an account id that belongs to
     * no account this closing has ever loaded — an account id is only ever trustworthy once the
     * closing's own account list has vouched for it. Filtering against `selectProjectAccounts` closes
     * the one path where a poll could keep itself alive on a closing that never produced the account it
     * is reacting to.
     *
     * ⚠️ ROUND 9 (contract-r8.md) — THE WATCH REPLACED `evidenceComplete`. The clause used to be
     * `row.evidenceComplete && row.reconciliation === 'NOT_ATTEMPTED'`, and that pair cannot occur on
     * the running stack: `EVIDENCE_RECONCILED` is the only writer of `evidenceComplete: true`, and it
     * moves `reconciliation` off `NOT_ATTEMPTED` in the same write (seed, initial be state and the
     * assignment gesture itself all leave the pair at `false`/`NOT_ATTEMPTED` — contract-r8.md (1)
     * names all four). So "filed, run still working" was byte-identical on the wire to "nothing filed
     * at all", and the chain ended on tick 1 every time (Demo Lauf 12, 06.09.2026: one write flipped
     * 1020 straight from `{NOT_ATTEMPTED, false}` to `{MATCHED, true}`, nothing in between). What tells
     * this build a run is under way now is the WRITE that starts one — `Assign Document` / `Accept
     * Proposal` — tracked in `project-accounts.reducer.ts`'s `reconciliationWatch`, not anything this
     * route answers. The naive alternative — drop `evidenceComplete` and poll while ANYTHING is
     * `NOT_ATTEMPTED` — is deliberately NOT what this is: the seed leaves accounts nobody has touched
     * `NOT_ATTEMPTED` forever, and that build would poll every one of them to the ceiling on every
     * entry (case 22).
     *
     * ⚠️ NOT SNAPSHOTTED. The counter route is FULFILLED (an effect answers it in-process); the list is
     * a REAL request the backend has to answer and a journal to decrypt. On a genuinely fresh entry the
     * counter route's tick 1 answer therefore routinely lands before the list has — reading
     * `selectProjectAccounts` at that instant with `withLatestFrom` found it still empty for THIS
     * closing (not stale: `loadAccounts` already cleared it) and rejected an account that was
     * perfectly real, ending the chain on tick 1 every time. So the openness check below WAITS for
     * `selectProjectAccountsStatus` to leave `'loading'`/`'idle'` for this exact `projectId` — no
     * further reconciliation-status request is spent while it waits, only the verdict on the one
     * already in hand is delayed. A cache-hit re-entry (list already `'loaded'`) resolves this
     * immediately, same as before.
     */
    public pollReconciliation$ = createEffect(() =>
        merge(
            this.actions$.pipe(
                ofType(
                    projectAccountsActions.loadReconciliationStatusSuccess,
                    projectAccountsActions.loadReconciliationStatusFailure,
                ),
            ),
            this.actions$.pipe(ofType(logout.success)),
        ).pipe(
            withLatestFrom(this.store.select(selectProjectAccountsProjectId)),
            // A late answer for a closing the user has left plans nothing — the same guard
            // `pollClassification$` applies to `loadSummarySuccess`. `logout.success` carries no
            // `projectId`, so it always passes here: whatever chain is current is exactly the one that
            // has to end.
            filter(([action, current]) => !('projectId' in action) || action.projectId === current),
            switchMap(([action, current]) => {
                if (!('projectId' in action)) {
                    // `logout.success` cancels whatever `timer()` was pending (this IS a new emission on
                    // the source `switchMap` is subscribed to) and ends the chain formally, if one was
                    // running at all.
                    return current === null
                        ? EMPTY
                        : of(projectAccountsActions.reconciliationPollEnded({ projectId: current }));
                }

                const { projectId, tick, elapsedMs } = action;
                const failed = !('status' in action);
                const consecutiveFailures = failed ? action.consecutiveFailures + 1 : 0;

                if (failed && consecutiveFailures >= RECONCILIATION_FAILURE_STREAK_LIMIT) {
                    // eslint-disable-next-line no-console
                    console.warn('abcp: reconciliation poll gave up after repeated failures');

                    return of(projectAccountsActions.reconciliationPollEnded({ projectId }));
                }

                const planNext = (
                    open: boolean,
                ): Observable<
                    ReturnType<
                        | typeof projectAccountsActions.reconciliationPollEnded
                        | typeof projectAccountsActions.loadReconciliationStatus
                    >
                > => {
                    if (!open || tick >= this.intervals.maxTicks) {
                        return of(projectAccountsActions.reconciliationPollEnded({ projectId }));
                    }

                    const delay =
                        elapsedMs < this.intervals.fastWindowMs ? this.intervals.fastMs : this.intervals.slowMs;

                    return timer(delay).pipe(
                        map(() =>
                            projectAccountsActions.loadReconciliationStatus({
                                projectId,
                                tick: tick + 1,
                                elapsedMs: elapsedMs + delay,
                                consecutiveFailures,
                            }),
                        ),
                    );
                };

                // A failure says nothing about which accounts are open, so it is treated as "keep
                // asking" rather than "nothing left" — only the failure streak or the tick ceiling may
                // end a failing chain. No list to wait for either: nothing here reads it.
                if (failed) {
                    return planNext(true);
                }

                return this.store.select(selectProjectAccountsStatus).pipe(
                    withLatestFrom(
                        this.store.select(selectProjectAccountsProjectId),
                        this.store.select(selectProjectAccounts),
                        this.store.select(selectReconciliationWatch),
                    ),
                    // Settled ('loaded' or 'failed') AND for THIS closing — 'idle'/'loading' means the
                    // list has not answered yet, and a project id that has since moved on again is a
                    // late arrival this tick no longer belongs to.
                    filter(([status, id]) => id === projectId && status !== 'loading' && status !== 'idle'),
                    take(1),
                    switchMap(([, , accounts, watch]) => {
                        // A single account id is known only once THIS closing's own list has answered
                        // it — an unknown id (round 4, SRR F2) never counts as "open", whether it is
                        // stale, belongs to another closing, or the list failed to load at all.
                        const knownAccountIds = new Set(accounts.map((account) => account.accountId));
                        // #162 round 9 — "this account is in the window" replaces `row.evidenceComplete`
                        // (contract-r8.md (4); see the ⚠️ above `pollReconciliation$` for why that pair
                        // could never fire). `NOT_ATTEMPTED` and `knownAccountIds` are unchanged.
                        const open = action.status.accounts.some(
                            (row) =>
                                watch.includes(row.accountId) &&
                                row.reconciliation === 'NOT_ATTEMPTED' &&
                                knownAccountIds.has(row.accountId),
                        );

                        return planNext(open);
                    }),
                );
            }),
        ),
    );

    /**
     * The one refetch of the expensive list, at the one moment it is safe — the reconciliation twin of
     * `reloadAfterClassification$` just above the summary effect below it.
     *
     * `pairwise`, so the condition is the TRANSITION (open → not open) and not the state: a plain screen
     * open where nothing is outstanding costs no second request. `reloadAccounts`, never
     * `loadAccountsIfNeeded` (plan:GH-162 P2) — the cache guard would silently serve the entry from the
     * store and the row would never pick up the new state.
     */
    public reloadAfterReconciliation$ = createEffect(() =>
        this.store.select(selectReconciliationOpen).pipe(
            pairwise(),
            filter(([before, after]) => before === true && after === false),
            withLatestFrom(this.store.select(selectProjectAccountsProjectId)),
            filter((pair): pair is [[boolean | null, boolean | null], string] => pair[1] !== null),
            map(([, projectId]) => projectAccountsActions.reloadAccounts({ projectId })),
        ),
    );

    public loadSummary$ = createEffect(() =>
        this.actions$.pipe(
            ofType(projectAccountsActions.loadSummary),
            switchMap(({ projectId }) =>
                this.service.getClassificationSummary(projectId).pipe(
                    map((summary) => projectAccountsActions.loadSummarySuccess({ projectId, summary })),
                    catchError((cause: unknown) =>
                        of(projectAccountsActions.loadSummaryFailure({ projectId, error: messageKeyOf(cause) })),
                    ),
                ),
            ),
        ),
    );

    /**
     * A manual assignment. `mergeMap`, not `switchMap`: the modal can submit several accounts in
     * sequence, and cancelling an in-flight write would silently drop an assignment the user made.
     */
    public writeBindings$ = createEffect(() =>
        this.actions$.pipe(
            ofType(projectAccountsActions.writeBindings),
            mergeMap(({ projectId, bindings }) =>
                this.service.writeBindings(projectId, bindings).pipe(
                    map((result) => projectAccountsActions.writeBindingsSuccess({ projectId, result })),
                    catchError((cause: unknown) =>
                        of(projectAccountsActions.writeBindingsFailure({ projectId, error: messageKeyOf(cause) })),
                    ),
                ),
            ),
        ),
    );

    /**
     * REV-22005 — a trustee's change to one account's state.
     *
     * `concatMap`, not `switchMap` and not `mergeMap`: a flag set and taken off in quick succession
     * must reach the server IN ORDER — `switchMap` would cancel the first request after the server may
     * already have applied it, and `mergeMap` could land the two answers the other way round, leaving
     * the row showing the state that was superseded.
     */
    public writeAccountState$ = createEffect(() =>
        this.actions$.pipe(
            ofType(projectAccountsActions.writeAccountState),
            concatMap(({ projectId, accountId, command }) =>
                this.service.writeAccountState(projectId, accountId, command).pipe(
                    map((patch) => projectAccountsActions.writeAccountStateSuccess({ projectId, patch })),
                    catchError((cause: unknown) =>
                        of(
                            projectAccountsActions.writeAccountStateFailure({
                                projectId,
                                accountId,
                                error: messageKeyOf(cause),
                            }),
                        ),
                    ),
                ),
            ),
        ),
    );

    /**
     * An explicit refetch, with the current view attached.
     *
     * ⚠️ THIS REPLACED A REFETCH AFTER EVERY WRITE (24.08.2026). A refetch per choice reloads the
     * whole table between two clicks — every position the trustee chose cost a journal decryption per
     * stored period — and makes the screen wait on the round trip before it shows the choice.
     *
     * The Kontenplan now shows each choice locally and asks for one refetch once the burst is over.
     * That moves the optimism into the container, where it belongs — the pure view stays controlled
     * and carries none of it.
     *
     * Carrying the current view is not decoration: a bare reload would answer the backend's DEFAULT —
     * the prior year alone, zero accounts filtered — and collapse the columns the user chose.
     */
    public reloadAccounts$ = createEffect(() =>
        this.actions$.pipe(
            ofType(projectAccountsActions.reloadAccounts),
            withLatestFrom(this.view$()),
            map(([{ projectId }, view]) => projectAccountsActions.loadAccounts({ projectId, ...view })),
        ),
    );

    /**
     * The ERP pull. `switchMap` would cancel a running sync on a double click, and the backend has
     * no lock — so a second pull would run against a half-written surface. `mergeMap` lets both
     * finish; the button is disabled while one is in flight.
     */
    public sync$ = createEffect(() =>
        this.actions$.pipe(
            ofType(projectAccountsActions.sync),
            mergeMap(({ projectId, body }) =>
                this.service.sync(projectId, body).pipe(
                    map((result) => projectAccountsActions.syncSuccess({ projectId, result })),
                    catchError((cause: unknown) =>
                        of(projectAccountsActions.syncFailure({ projectId, error: messageKeyOf(cause) })),
                    ),
                ),
            ),
        ),
    );

    /**
     * REV-22007 — the FILE closing's re-import. The upload stored the file seconds ago, so the first
     * attempt usually meets the virus scan still running (409 `scanPending`). That one refusal is
     * retried with backoff while the progress modal keeps showing "loading"; every other refusal
     * (quarantined, a file the rules refuse, a foreign id) ends it at once — waiting cannot change it.
     *
     * `mergeMap` for the reason `sync$` gives: a second click must not cancel a write under way.
     */
    public syncFromFile$ = createEffect(() =>
        this.actions$.pipe(
            ofType(projectAccountsActions.syncFromFile),
            mergeMap(({ projectId, fileId }) =>
                this.service.syncFromFile(projectId, fileId).pipe(
                    retry({
                        count: SCAN_RETRY_MAX,
                        delay: (cause: unknown, attempt: number) =>
                            isScanPending(cause) ? timer(scanPendingRetryDelayMs(attempt)) : throwError(() => cause),
                    }),
                    map((result) => projectAccountsActions.syncSuccess({ projectId, result })),
                    catchError((cause: unknown) =>
                        of(projectAccountsActions.syncFailure({ projectId, error: messageKeyOf(cause) })),
                    ),
                ),
            ),
        ),
    );

    /**
     * A finished sync means new accounts, new balances and possibly a new PERIOD — a gap fill writes
     * a journal that did not exist before, so the period list is refetched too. Without that the
     * chip for a period just filled would not appear until the screen is reopened.
     */
    public reloadAfterSync$ = createEffect(() =>
        this.actions$.pipe(
            ofType(projectAccountsActions.syncSuccess),
            withLatestFrom(this.view$()),
            mergeMap(([{ projectId }, view]) => [
                projectAccountsActions.loadAccounts({ projectId, ...view }),
                projectAccountsActions.loadSummary({ projectId }),
                projectAccountsActions.loadJournalPeriods({ projectId }),
            ]),
        ),
    );
}
